import React, { useState, useEffect, useCallback } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';

const Reports = () => {
  const [jobs, setJobs] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [customers, setCustomers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [reportType, setReportType] = useState('daily');
  const [reportPeriod, setReportPeriod] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [reportData, setReportData] = useState(null);
  const [completedOnly, setCompletedOnly] = useState(false);
  const [monthEndMonth, setMonthEndMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  // Real-time listeners
  useEffect(() => {
    const unsubscribers = [];

    // Jobs listener
    const jobsQuery = query(
      collection(db, 'jobs'),
      orderBy('scheduledDate', 'desc')
    );
    const jobsUnsub = onSnapshot(jobsQuery, (snapshot) => {
      const jobsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setJobs(jobsData);
    });
    unsubscribers.push(jobsUnsub);

    // Customers listener
    const customersUnsub = onSnapshot(collection(db, 'customers'), (snapshot) => {
      const customersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(customersData);
    });
    unsubscribers.push(customersUnsub);

    // Expenses listener
    const expensesQuery = query(
      collection(db, 'expenses'),
      orderBy('date', 'desc')
    );
    const expensesUnsub = onSnapshot(expensesQuery, (snapshot) => {
      setExpenses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    unsubscribers.push(expensesUnsub);

    return () => unsubscribers.forEach(unsub => unsub());
  }, []);

  const generateReport = useCallback(() => {
    let filteredJobs = [];

    switch (reportType) {
      case 'daily':
        filteredJobs = jobs.filter(job => job.scheduledDate === reportPeriod);
        break;
      case 'weekly':
        const weekStart = new Date(reportPeriod);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        filteredJobs = jobs.filter(job => {
          const jobDate = new Date(job.scheduledDate);
          return jobDate >= weekStart && jobDate <= weekEnd;
        });
        break;
      case 'monthly':
        const [year, month] = reportPeriod.split('-');
        filteredJobs = jobs.filter(job => {
          const [jobYear, jobMonth] = job.scheduledDate.split('-');
          return jobYear === year && jobMonth === month;
        });
        break;
      case 'yearly':
        const reportYear = reportPeriod.split('-')[0];
        filteredJobs = jobs.filter(job => {
          const jobYear = job.scheduledDate.split('-')[0];
          return jobYear === reportYear;
        });
        break;
      default:
        filteredJobs = jobs;
    }

    if (completedOnly) {
      filteredJobs = filteredJobs.filter(job => job.status === 'completed');
    }

    // Filter expenses by same period
    let filteredExpenses = [];
    const expDateStr = (exp) => (exp.date && typeof exp.date === 'string') ? exp.date.slice(0, 10) : (exp.date ? new Date(exp.date).toISOString().slice(0, 10) : '');
    switch (reportType) {
      case 'daily':
        filteredExpenses = expenses.filter(exp => expDateStr(exp) === reportPeriod);
        break;
      case 'weekly': {
        const weekStart = new Date(reportPeriod);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        filteredExpenses = expenses.filter(exp => {
          const d = expDateStr(exp);
          if (!d) return false;
          const expDate = new Date(d);
          return expDate >= weekStart && expDate <= weekEnd;
        });
        break;
      }
      case 'monthly': {
        const [y, m] = reportPeriod.split('-');
        filteredExpenses = expenses.filter(exp => {
          const d = expDateStr(exp);
          if (!d) return false;
          const [ey, em] = d.split('-');
          return ey === y && em === m;
        });
        break;
      }
      case 'yearly': {
        const reportYear = reportPeriod.split('-')[0];
        filteredExpenses = expenses.filter(exp => {
          const d = expDateStr(exp);
          return d && d.slice(0, 4) === reportYear;
        });
        break;
      }
      default:
        filteredExpenses = expenses;
    }

    // Calculate totals
    const totalJobs = filteredJobs.length;
    const completedJobs = filteredJobs.filter(job => job.status === 'completed').length;
    const totalRevenue = filteredJobs.reduce((sum, job) => sum + (parseFloat(job.price) || 0), 0);
    const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
    const netRevenue = totalRevenue - totalExpenses;

    // Group by customer
    const customerStats = {};
    filteredJobs.forEach(job => {
      if (!customerStats[job.customerId]) {
        customerStats[job.customerId] = {
          customerName: job.customerName,
          jobs: [],
          totalSpent: 0,
          completedJobs: 0
        };
      }

      customerStats[job.customerId].jobs.push(job);
      customerStats[job.customerId].totalSpent += parseFloat(job.price) || 0;
      if (job.status === 'completed') {
        customerStats[job.customerId].completedJobs += 1;
      }
    });

    // Group by service type
    const serviceStats = {};
    filteredJobs.forEach(job => {
      if (!serviceStats[job.serviceType]) {
        serviceStats[job.serviceType] = {
          count: 0,
          revenue: 0,
          completed: 0
        };
      }

      serviceStats[job.serviceType].count += 1;
      serviceStats[job.serviceType].revenue += parseFloat(job.price) || 0;
      if (job.status === 'completed') {
        serviceStats[job.serviceType].completed += 1;
      }
    });

    setReportData({
      totalJobs,
      completedJobs,
      totalRevenue,
      totalExpenses,
      netRevenue,
      filteredExpenses,
      customerStats,
      serviceStats,
      filteredJobs
    });
  }, [jobs, expenses, reportType, reportPeriod, completedOnly]);

  // Generate report when data or filters change
  useEffect(() => {
    generateReport();
  }, [generateReport]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Completed jobs for selected month (for month-end / QuickBooks)
  const getCompletedJobsForMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-').map(Number);
    return jobs.filter(job => {
      if (job.status !== 'completed') return false;
      const jobDate = job.completedAt ? new Date(job.completedAt.seconds ? job.completedAt.seconds * 1000 : job.completedAt) : new Date(job.scheduledDate);
      return jobDate.getFullYear() === year && jobDate.getMonth() + 1 === month;
    });
  };

  const monthEndCompletedJobs = getCompletedJobsForMonth(monthEndMonth);

  // Export CSV for QuickBooks (Date, Customer, Service Type, Description, Amount)
  const exportQuickBooksCSV = () => {
    const rows = monthEndCompletedJobs.map(job => {
      const dateStr = job.completedAt
        ? (job.completedAt.seconds ? new Date(job.completedAt.seconds * 1000) : new Date(job.completedAt)).toLocaleDateString('en-CA')
        : (job.scheduledDate || '').slice(0, 10);
      const desc = [job.serviceType, job.description].filter(Boolean).join(' - ');
      const amount = parseFloat(job.price) || 0;
      return { Date: dateStr, Customer: job.customerName || '', 'Service Type': job.serviceType || '', Description: desc, Amount: amount.toFixed(2) };
    });
    const headers = ['Date', 'Customer', 'Service Type', 'Description', 'Amount'];
    const csvContent = [
      headers.join(','),
      ...rows.map(r => headers.map(h => `"${String(r[h] || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `completed-jobs-${monthEndMonth}-quickbooks.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const setReportTypeAndPeriod = (type) => {
    setReportType(type);
    const d = new Date();
    if (type === 'daily') setReportPeriod(d.toISOString().split('T')[0]);
    else if (type === 'weekly') setReportPeriod(d.toISOString().split('T')[0]);
    else if (type === 'monthly') setReportPeriod(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    else if (type === 'yearly') setReportPeriod(String(d.getFullYear()));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-3xl font-bold text-secondary-900 mb-2">
          Reports
        </h1>
        <p className="text-secondary-600 text-sm sm:text-base">
          Daily, weekly, and monthly reporting. Export for QuickBooks.
        </p>
      </div>

      {/* Daily / Weekly / Monthly report selector */}
      <div className="card mb-6">
        <div className="p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">Report period</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { value: 'daily', label: 'Daily' },
              { value: 'weekly', label: 'Weekly' },
              { value: 'monthly', label: 'Monthly' },
              { value: 'yearly', label: 'Yearly' }
            ].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setReportTypeAndPeriod(value)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  reportType === value
                    ? 'bg-primary-600 text-white'
                    : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-end">
            <div className="w-full sm:w-auto sm:min-w-[180px]">
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                {reportType === 'daily' ? 'Date' :
                 reportType === 'weekly' ? 'Any day in week' :
                 reportType === 'monthly' ? 'Month' : 'Year'}
              </label>
              {reportType === 'monthly' ? (
                <input
                  type="month"
                  value={reportPeriod}
                  onChange={(e) => setReportPeriod(e.target.value)}
                  className="input w-full min-h-[44px]"
                />
              ) : reportType === 'yearly' ? (
                <input
                  type="number"
                  value={reportPeriod.split('-')[0]}
                  onChange={(e) => setReportPeriod(String(e.target.value))}
                  className="input w-full min-h-[44px]"
                  min={2020}
                  max={new Date().getFullYear() + 1}
                />
              ) : (
                <input
                  type="date"
                  value={reportPeriod}
                  onChange={(e) => setReportPeriod(e.target.value)}
                  className="input w-full min-h-[44px]"
                />
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-secondary-700 cursor-pointer">
              <input
                type="checkbox"
                checked={completedOnly}
                onChange={(e) => setCompletedOnly(e.target.checked)}
                className="rounded text-primary-600"
              />
              Completed jobs only
            </label>
          </div>
        </div>
      </div>

      {/* Month-end: Completed jobs for QuickBooks */}
      <div className="card mb-8">
        <div className="p-4 sm:p-6">
          <h2 className="text-xl font-semibold text-secondary-900 mb-4">Month-end: Completed jobs for QuickBooks</h2>
          <p className="text-secondary-600 text-sm mb-4">
            Completed jobs for the selected month. Export CSV to import into QuickBooks (sales/deposits).
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-4 mb-4">
            <div className="w-full sm:w-auto sm:min-w-[160px]">
              <label className="block text-sm font-medium text-secondary-700 mb-1">Month</label>
              <input
                type="month"
                value={monthEndMonth}
                onChange={(e) => setMonthEndMonth(e.target.value)}
                className="input w-full min-h-[44px] touch-manipulation"
              />
            </div>
            <button onClick={exportQuickBooksCSV} className="btn-primary w-full sm:w-auto min-h-[44px] touch-manipulation">
              Export CSV for QuickBooks
            </button>
          </div>
          <div className="flex flex-wrap gap-6 text-sm mb-4">
            <span className="text-secondary-700">
              <strong>{monthEndCompletedJobs.length}</strong> completed jobs
            </span>
            <span className="text-green-600 font-semibold">
              Total: {formatCurrency(monthEndCompletedJobs.reduce((s, j) => s + (parseFloat(j.price) || 0), 0))}
            </span>
          </div>
          <div className="overflow-x-auto border border-secondary-200 rounded-lg">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Customer</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Service</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-secondary-600 uppercase">Description</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-secondary-600 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {monthEndCompletedJobs.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-secondary-500">No completed jobs for this month</td></tr>
                ) : (
                  monthEndCompletedJobs.map((job) => {
                    const dateStr = job.completedAt
                      ? (job.completedAt.seconds ? new Date(job.completedAt.seconds * 1000) : new Date(job.completedAt)).toLocaleDateString('en-US')
                      : (job.scheduledDate || '').slice(0, 10);
                    return (
                      <tr key={job.id}>
                        <td className="px-4 py-2 text-sm text-secondary-900">{dateStr}</td>
                        <td className="px-4 py-2 text-sm text-secondary-900">{job.customerName || '—'}</td>
                        <td className="px-4 py-2 text-sm text-secondary-700">{job.serviceType || '—'}</td>
                        <td className="px-4 py-2 text-sm text-secondary-600 max-w-xs truncate" title={job.description}>{job.description || '—'}</td>
                        <td className="px-4 py-2 text-sm text-right font-medium text-green-600">{formatCurrency(job.price)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {reportData && (
        <div className="space-y-8">
          <h2 className="text-lg font-semibold text-secondary-800">
            {reportType === 'daily' && `${formatDate(reportPeriod)}`}
            {reportType === 'weekly' && (() => {
              const weekStart = new Date(reportPeriod);
              weekStart.setDate(weekStart.getDate() - weekStart.getDay());
              const weekEnd = new Date(weekStart);
              weekEnd.setDate(weekStart.getDate() + 6);
              return `${formatDate(weekStart.toISOString().split('T')[0])} – ${formatDate(weekEnd.toISOString().split('T')[0])}`;
            })()}
            {reportType === 'monthly' && (() => {
              const [y, m] = reportPeriod.split('-');
              return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            })()}
            {reportType === 'yearly' && reportPeriod}
          </h2>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            <div className="card">
              <div className="p-4 sm:p-6">
                <div className="text-2xl sm:text-3xl font-bold text-primary-600">{reportData.totalJobs}</div>
                <div className="text-sm text-secondary-600">Total Jobs</div>
              </div>
            </div>
            <div className="card">
              <div className="p-4 sm:p-6">
                <div className="text-2xl sm:text-3xl font-bold text-green-600">{reportData.completedJobs}</div>
                <div className="text-sm text-secondary-600">Completed</div>
              </div>
            </div>
            <div className="card">
              <div className="p-4 sm:p-6">
                <div className="text-2xl sm:text-3xl font-bold text-blue-600">
                  {formatCurrency(reportData.totalRevenue)}
                </div>
                <div className="text-sm text-secondary-600">Revenue</div>
              </div>
            </div>
            <div className="card">
              <div className="p-4 sm:p-6">
                <div className="text-2xl sm:text-3xl font-bold text-red-600">
                  {formatCurrency(reportData.totalExpenses ?? 0)}
                </div>
                <div className="text-sm text-secondary-600">Expenses</div>
              </div>
            </div>
            <div className="card">
              <div className="p-4 sm:p-6">
                <div className={`text-2xl sm:text-3xl font-bold ${(reportData.netRevenue ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(reportData.netRevenue ?? 0)}
                </div>
                <div className="text-sm text-secondary-600">Net</div>
              </div>
            </div>
          </div>

          {/* Customer Breakdown */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Customer Breakdown</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Jobs
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Completed
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Total Spent
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {Object.entries(reportData.customerStats).map(([customerId, stats]) => (
                      <tr key={customerId}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                          {stats.customerName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {stats.jobs.length}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {stats.completedJobs}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                          {formatCurrency(stats.totalSpent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Service Breakdown */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Service Breakdown</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Service Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Jobs
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Completed
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Revenue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                        Avg price
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {Object.entries(reportData.serviceStats).map(([serviceType, stats]) => (
                      <tr key={serviceType}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                          {serviceType}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {stats.count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {stats.completed}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                          {formatCurrency(stats.revenue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                          {stats.count ? formatCurrency(stats.revenue / stats.count) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Expenses for period */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Expenses for period</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-secondary-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-secondary-500 uppercase">Description</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-secondary-500 uppercase">Category</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-secondary-500 uppercase">Customer / Job</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-secondary-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {(!reportData.filteredExpenses || reportData.filteredExpenses.length === 0) ? (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-secondary-500">No expenses in this period</td></tr>
                    ) : (
                      reportData.filteredExpenses.map((exp) => (
                        <tr key={exp.id}>
                          <td className="px-4 py-2 text-sm text-secondary-900 whitespace-nowrap">
                            {exp.date ? new Date(exp.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </td>
                          <td className="px-4 py-2 text-sm text-secondary-900">{exp.description || '—'}</td>
                          <td className="px-4 py-2 text-sm text-secondary-600">{exp.category || '—'}</td>
                          <td className="px-4 py-2 text-sm text-secondary-600">
                            {exp.customerName ? `${exp.customerName}${exp.jobDescription ? ` · ${exp.jobDescription}` : ''}` : '—'}
                          </td>
                          <td className="px-4 py-2 text-sm text-right font-medium text-red-600">{formatCurrency(exp.amount)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {reportData.filteredExpenses && reportData.filteredExpenses.length > 0 && (
                <p className="mt-3 text-sm font-semibold text-secondary-700">
                  Period expenses total: <span className="text-red-600">{formatCurrency(reportData.totalExpenses)}</span>
                </p>
              )}
            </div>
          </div>

          {/* Detailed Job List */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Job Details</h3>
              <div className="space-y-4">
                {reportData.filteredJobs.map((job) => (
                  <div key={job.id} className="border border-secondary-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <h4 className="font-semibold text-secondary-900">{job.customerName}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          job.status === 'completed' ? 'bg-green-100 text-green-800' :
                          job.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                          job.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {job.status}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-green-600">{formatCurrency(job.price)}</div>
                        <div className="text-sm text-secondary-500">{formatDate(job.scheduledDate)}</div>
                      </div>
                    </div>
                    <div className="text-sm text-secondary-600">
                      <span className="font-medium">Service:</span> {job.serviceType}
                    </div>
                    {job.description && (
                      <div className="text-sm text-secondary-600 mt-1">
                        <span className="font-medium">Description:</span> {job.description}
                      </div>
                    )}
                  </div>
                ))}
                {reportData.filteredJobs.length === 0 && (
                  <div className="text-center py-8 text-secondary-500">
                    No jobs found for the selected period
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;